import {
  useApiClient,
  WebshopFavoritesCommands,
  WebshopFavoritesEvent,
  WebshopFavoritesProduct,
  WebshopFavoritesQueries,
  WebshopFavoritesVersioned,
  webshopFavoritesRemoveProductService,
} from '@cldsystems/ws-api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { FavoritesActionButtonsEnum } from '@/types/action-buttons/action-buttons';
import { UpdateMutateProps } from '@/types/common';
import { isNoChangesError, isOptimisticLockError } from '@/utils/get-api-error-message';

type FavoritesRemoveProductMutationPayload = {
  productId: string;
  event: WebshopFavoritesEvent;
  product: WebshopFavoritesProduct;
  operationId: string;
  buttonType?: FavoritesActionButtonsEnum;
  optimisticApplied?: boolean;
  previousProduct?: WebshopFavoritesProduct;
  /** Skip the error toast for a (retryable) optimistic-lock conflict. */
  suppressVersionConflictToast?: boolean;
};

type FavoritesRemoveProductMutationContext = {
  favoritesId: string;
  productId: string;
  product: WebshopFavoritesProduct;
  operationId: string;
  previousProduct: WebshopFavoritesProduct | undefined;
  previousVersion?: number;
};

const useFavoritesRemoveProductMutate = (
  markFavoritePending: (
    productId: string,
    requestedAction: 'add' | 'remove',
    buttonType: FavoritesActionButtonsEnum | undefined,
    operationId: string,
  ) => void,
  clearFavoritePending: (productId: string, operationId?: string) => void,
  getFavoritePending: (productId: string) =>
    | {
        operationId: string;
      }
    | undefined,
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { hasSchema, httpClient } = useApiClient();

  return useMutation({
    mutationKey: [WebshopFavoritesCommands.RemoveProduct],
    mutationFn: ({ params, payload }: UpdateMutateProps<FavoritesRemoveProductMutationPayload>) =>
      webshopFavoritesRemoveProductService({
        httpClient,
        hasSchema: Boolean(hasSchema),
      })({ params, payload: { productId: payload!.productId, event: payload!.event } }),
    onMutate: async ({ params, payload }) => {
      if (!payload) return;

      markFavoritePending(payload.productId, 'remove', payload.buttonType, payload.operationId);

      const queryKey = [WebshopFavoritesQueries.GetById, params.id];
      await queryClient.cancelQueries({ queryKey });
      const previousFavorites = queryClient.getQueryData<WebshopFavoritesVersioned>(queryKey);
      const previousProduct = payload.previousProduct;

      queryClient.setQueryData<WebshopFavoritesVersioned>(queryKey, old => {
        if (!old) return undefined;

        const products = payload.optimisticApplied
          ? old.favorites.products
          : old.favorites.products.filter(p => p.id !== payload.productId);

        return {
          ...old,
          version: old.version + 1,
          favorites: {
            ...old.favorites,
            products,
          },
        };
      });

      return {
        favoritesId: params.id,
        productId: payload.productId,
        product: payload.product,
        operationId: payload.operationId,
        previousProduct,
        previousVersion: previousFavorites?.version,
      } satisfies FavoritesRemoveProductMutationContext;
    },
    onError: (err, variables, context) => {
      if (context) {
        const currentPending = getFavoritePending(context.productId);

        if (!currentPending || currentPending.operationId === context.operationId) {
          const queryKey = [WebshopFavoritesQueries.GetById, context.favoritesId];
          queryClient.setQueryData<WebshopFavoritesVersioned>(queryKey, old => {
            if (!old || !context.previousProduct) {
              return old;
            }

            const isProductExists = old.favorites.products.some(
              product => product.id === context.productId,
            );

            if (isProductExists) {
              return old;
            }

            return {
              ...old,
              version: context.previousVersion ?? old.version,
              favorites: {
                ...old.favorites,
                products: [...old.favorites.products, context.previousProduct],
              },
            };
          });
        }

        // No-op commands ("no changes detected") are harmless. Optimistic-lock
        // conflicts are retried with the real version by the caller; stay silent
        // on the first failure so we only notify if the retry also fails.
        const isSuppressedVersionConflict =
          variables.payload?.suppressVersionConflictToast && isOptimisticLockError(err);
        if (!isNoChangesError(err) && !isSuppressedVersionConflict) {
          toast.error(t?.common.productRemoveFromFavoritesFailed);
        }
      }
      if (context) {
        clearFavoritePending(context.productId, context.operationId);
      }
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context) {
        clearFavoritePending(context.productId, context.operationId);
      }
    },
  });
};

export default useFavoritesRemoveProductMutate;
